// src/pages/ELearning/RatingPage.tsx — DESIGN v2
import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { Loader2, Trophy, Medal, Crown, Flame, TrendingUp, Award } from 'lucide-react';

const MEDALS = ['🥇', '🥈', '🥉'];
const PODIUM_COLORS = [
  'linear-gradient(135deg, #f6d365 0%, #fda085 100%)', // gold
  'linear-gradient(135deg, #c0c0c0 0%, #e8e8e8 100%)', // silver
  'linear-gradient(135deg, #cd7f32 0%, #e8a87c 100%)', // bronze
];

const RANK_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #2575fc 0%, #6a11cb 100%)',
  'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
];

const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
};

const RatingPage: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState<number | null>(null);

  useEffect(() => {
    const fetchRating = async () => {
      try {
        const data = await api.get('/elearning/rating/top-company').then(r => r.data);
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRating();
  }, []);

  if (loading) {
    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 80 }}>
          <Loader2 size={40} className="spin" color="#667eea" />
        </div>
      </>
    );
  }

  const top3 = users.slice(0, 3);
  const rest = users.slice(3);
  const maxRating = users[0]?.elearning?.rating || 1;

  // Порядок подиума: 2, 1, 3
  const podiumOrder = [top3[1], top3[0], top3[2]].filter(Boolean);
  const podiumOriginalIdx = [1, 0, 2];

  return (
    <>
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.08); }
        }
        .rating-card {
          transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .rating-card:hover {
          transform: translateY(-6px);
          box-shadow: 0 20px 50px rgba(31, 38, 135, 0.15) !important;
        }
        .podium-card {
          animation: fadeUp 0.6s ease-out backwards;
        }
        .podium-card:nth-child(1) { animation-delay: 0.1s; }
        .podium-card:nth-child(2) { animation-delay: 0.2s; }
        .podium-card:nth-child(3) { animation-delay: 0.3s; }
        .medal-icon {
          animation: pulse 2.5s ease-in-out infinite;
        }
      `}</style>

      {/* ═══ Hero ═══ */}
      <div style={{
        marginBottom: 32,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        animation: 'fadeUp 0.5s ease-out',
      }}>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: 20,
          background: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 12px 32px rgba(246, 211, 101, 0.4)',
        }}>
          <Trophy size={32} color="#fff" />
        </div>
        <div>
          <h1 style={{
            fontSize: 32,
            fontWeight: 800,
            margin: 0,
            background: 'linear-gradient(135deg, #1a1a2e 0%, #667eea 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '-0.5px',
          }}>
            Рейтинг сотрудников
          </h1>
          <p style={{ color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 }}>
            Топ {users.length} по баллам обучения
          </p>
        </div>
      </div>

      {/* ═══ PODIUM ТОП-3 ═══ */}
      {top3.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: podiumOrder.length === 3 ? '1fr 1.15fr 1fr' : `repeat(${podiumOrder.length}, 1fr)`,
          gap: 16,
          marginBottom: 32,
          alignItems: 'end',
        }}>
          {podiumOrder.map((user, i) => {
            if (!user) return null;
            const realIdx = podiumOriginalIdx[i];
            const isFirst = realIdx === 0;
            const rating = user.elearning?.rating || 0;
            const points = user.elearning?.totalPoints || 0;
            const streak = user.elearning?.streak || 0;

            return (
              <div
                key={user._id || i}
                className="podium-card"
                onMouseEnter={() => setHovered(realIdx)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  background: 'rgba(255, 255, 255, 0.55)',
                  backdropFilter: 'blur(24px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                  border: '1px solid rgba(255, 255, 255, 0.7)',
                  borderRadius: 28,
                  padding: isFirst ? '32px 24px' : '24px 20px',
                  textAlign: 'center',
                  boxShadow: isFirst
                    ? '0 20px 60px rgba(246, 211, 101, 0.25), 0 4px 20px rgba(0,0,0,0.04)'
                    : '0 8px 32px rgba(31, 38, 135, 0.08)',
                  transform: isFirst ? 'translateY(-12px)' : 'translateY(0)',
                  transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Gradient top strip */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 4,
                  background: PODIUM_COLORS[realIdx],
                }} />

                {/* Medal */}
                <div
                  className="medal-icon"
                  style={{
                    fontSize: isFirst ? 56 : 44,
                    lineHeight: 1,
                    marginBottom: 12,
                    filter: 'drop-shadow(0 4px 12px rgba(246, 211, 101, 0.4))',
                  }}
                >
                  {MEDALS[realIdx]}
                </div>

                {/* Avatar */}
                <div style={{
                  width: isFirst ? 88 : 72,
                  height: isFirst ? 88 : 72,
                  borderRadius: '50%',
                  background: PODIUM_COLORS[realIdx],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 14px',
                  color: '#fff',
                  fontSize: isFirst ? 32 : 26,
                  fontWeight: 800,
                  letterSpacing: 1,
                  boxShadow: `0 8px 24px rgba(0,0,0,0.12)`,
                  border: '3px solid rgba(255,255,255,0.8)',
                }}>
                  {getInitials(user.name)}
                </div>

                {/* Name */}
                <div style={{
                  fontSize: isFirst ? 20 : 17,
                  fontWeight: 700,
                  color: '#1a1a2e',
                  marginBottom: 4,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {user.name || 'Сотрудник'}
                </div>

                {/* Rating big */}
                <div style={{
                  fontSize: isFirst ? 36 : 28,
                  fontWeight: 800,
                  background: PODIUM_COLORS[realIdx],
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1.1,
                  marginBottom: 8,
                }}>
                  {rating}
                </div>

                <div style={{
                  fontSize: 11,
                  color: '#8b8fa8',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 14,
                }}>
                  Рейтинг
                </div>

                {/* Extra stats */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 16,
                  fontSize: 12,
                  color: '#5a5f7a',
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <Award size={14} color="#667eea" /> {points}
                  </span>
                  {streak > 0 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Flame size={14} color="#f59e0b" /> {streak} дн.
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ ОСТАЛЬНЫЕ МЕСТА ═══ */}
      {rest.length > 0 && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.6)',
          borderRadius: 24,
          padding: 8,
          boxShadow: '0 8px 32px rgba(31, 38, 135, 0.06)',
        }}>
          {rest.map((user, i) => {
            const rank = i + 4;
            const rating = user.elearning?.rating || 0;
            const points = user.elearning?.totalPoints || 0;
            const streak = user.elearning?.streak || 0;
            const pct = maxRating > 0 ? Math.min(100, (rating / maxRating) * 100) : 0;
            const gradIdx = i % RANK_GRADIENTS.length;
            const isHover = hovered === rank - 1;

            return (
              <div
                key={user._id || rank}
                className="rating-card"
                onMouseEnter={() => setHovered(rank - 1)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                  padding: '14px 16px',
                  borderRadius: 18,
                  margin: '2px 0',
                  background: isHover ? 'rgba(255,255,255,0.6)' : 'transparent',
                  animation: `fadeUp 0.4s ease-out ${0.05 * i}s backwards`,
                }}
              >
                {/* Rank badge */}
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: 12,
                  background: RANK_GRADIENTS[gradIdx],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: 15,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  flexShrink: 0,
                }}>
                  {rank}
                </div>

                {/* Avatar small */}
                <div style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: RANK_GRADIENTS[gradIdx],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0,
                  opacity: 0.85,
                }}>
                  {getInitials(user.name)}
                </div>

                {/* Name + progress */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: 15,
                    fontWeight: 600,
                    color: '#1a1a2e',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginBottom: 6,
                  }}>
                    {user.name || 'Сотрудник'}
                  </div>
                  <div style={{
                    height: 6,
                    background: 'rgba(102, 126, 234, 0.1)',
                    borderRadius: 3,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${pct}%`,
                      background: RANK_GRADIENTS[gradIdx],
                      borderRadius: 3,
                      transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    }} />
                  </div>
                </div>

                {/* Streak */}
                {streak > 0 && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    padding: '4px 10px',
                    background: 'rgba(245, 158, 11, 0.12)',
                    color: '#f59e0b',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}>
                    <Flame size={12} /> {streak}
                  </div>
                )}

                {/* Points */}
                <div style={{
                  textAlign: 'right',
                  flexShrink: 0,
                  minWidth: 80,
                }}>
                  <div style={{
                    fontSize: 17,
                    fontWeight: 800,
                    background: RANK_GRADIENTS[gradIdx],
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}>
                    {rating}
                  </div>
                  <div style={{ fontSize: 10, color: '#8b8fa8', fontWeight: 600 }}>
                    {points} очков
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ Пустое состояние ═══ */}
      {users.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: 80,
          background: 'rgba(255,255,255,0.5)',
          backdropFilter: 'blur(20px)',
          borderRadius: 24,
          border: '1px dashed rgba(102, 126, 234, 0.3)',
        }}>
          <Trophy size={56} color="#667eea" style={{ opacity: 0.4 }} />
          <h3 style={{ color: '#5a5f7a', marginTop: 16, fontWeight: 600 }}>
            Пока нет данных рейтинга
          </h3>
          <p style={{ color: '#8b8fa8', fontSize: 14 }}>
            Пройдите первый тест, чтобы появиться в рейтинге
          </p>
        </div>
      )}
    </>
  );
};

export default RatingPage;
